import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import type { UploadedFile } from "@/lib/catalog/types";
import { cn } from "@/lib/utils";

type AdListingImagePickerProps = {
  images: UploadedFile[];
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  onSelectAll: () => void;
  onClearAll: () => void;
  disabled?: boolean;
  className?: string;
};

export function AdListingImagePicker({
  images,
  selectedIds,
  onToggle,
  onSelectAll,
  onClearAll,
  disabled,
  className,
}: AdListingImagePickerProps) {
  const visible = images.filter((img) => Boolean(img.url));
  const selectedCount = visible.filter((img) => selectedIds.has(img.id)).length;

  if (visible.length === 0) {
    return (
      <p className={cn("text-sm text-muted-foreground", className)}>
        This product has no gallery images yet. Add images on the product form first.
      </p>
    );
  }

  return (
    <div className={cn("space-y-3", className)}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold text-foreground">
          Images for this ad{" "}
          <span className="font-normal text-muted-foreground">
            ({selectedCount} of {visible.length} selected)
          </span>
        </p>
        <div className="flex items-center gap-2">
          <Button type="button" variant="ghost" size="sm" className="h-8 text-xs" disabled={disabled} onClick={onSelectAll}>
            Select all
          </Button>
          <Button type="button" variant="ghost" size="sm" className="h-8 text-xs" disabled={disabled} onClick={onClearAll}>
            Clear
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 max-h-[min(42vh,22rem)] overflow-y-auto pr-1">
        {visible.map((img) => {
          const checked = selectedIds.has(img.id);
          return (
            <button
              key={img.id}
              type="button"
              disabled={disabled}
              onClick={() => onToggle(img.id)}
              className={cn(
                "group relative aspect-[4/3] overflow-hidden rounded-lg border text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                checked ? "border-primary ring-2 ring-primary/30" : "border-border/60 hover:border-primary/40",
                disabled && "opacity-60 cursor-not-allowed",
              )}
              aria-pressed={checked}
              aria-label={`${checked ? "Deselect" : "Select"} ${img.filename}`}
            >
              <img src={img.url!} alt={img.filename} className="h-full w-full object-cover" loading="lazy" />
              <span className="absolute inset-0 bg-black/0 transition-colors group-hover:bg-black/10" aria-hidden />
              <span className="pointer-events-none absolute left-2 top-2 flex h-6 w-6 items-center justify-center rounded-md bg-black/45 backdrop-blur-sm">
                <Checkbox checked={checked} tabIndex={-1} aria-hidden className="pointer-events-none border-white/70 data-[state=checked]:border-primary data-[state=checked]:bg-primary" />
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
